import { BuildingOfficeIcon } from "@heroicons/react/24/outline";
import { useConfigurations } from "@via/api/configuration.tsx";
import Modal from "@via/components/modal.tsx";
import { useAuth } from "@via/hooks/useAuth.tsx";
import { useLogin } from "@via/api/auth.api.tsx";
import Config from "@via/utils/config";
import { AUTH_LINKS, ROUTES } from "@via/utils/constants.tsx";
import { loginOptions } from "@via/utils/form-validators.tsx";
import { shouldBlockAccess } from "@via/utils/helpers";
import { type LoginPayload } from "@via/utils/interfaces.tsx";
import { AuthTypes } from "common";
import { useEffect, useState, type JSX } from "react";
import { useForm } from "react-hook-form";
import { Link, Navigate, useLocation, useSearchParams } from "react-router-dom";
import { useLocalStorage } from "usehooks-ts";
import "./authentication.css";
import GithubLogo from "/github-logo.svg";
import GoogleLogo from "/google-logo.svg";
import Logo from "/logo.svg";
import SamlLogo from "/saml-logo.svg";
import { createMarkup } from "@via/utils/html-utils";
import { MessageBanner } from "@via/components/message-banner/message-banner";
import { BannerType } from "@via/components/message-banner/message-banner.tsx";
import {
  isBackendRoute,
  normalizeRedirectPath,
  setPostLoginRedirect,
} from "@via/utils/post-login-redirect";
import { BackendRedirect } from "@via/utils/backend-redirect";
import { EyeIcon, EyeSlashIcon } from "@heroicons/react/24/solid";

export default function Login({
  selfSignIn = false,
  email,
  organizationName,
  token = "",
  onSuccess,
}: {
  selfSignIn?: boolean;
  email?: string;
  organizationName?: string;
  token?: string;
  onSuccess?(): void;
}): JSX.Element {
  const { pathname, search } = useLocation();
  const [searchParams] = useSearchParams();
  // searchParams.get already decodes the value buildLoginUrl encoded once. A
  // second decode would turn an encoded %2F, %25, ? or # in the target into a
  // different path, so the guard and navigate() both see this value as is.
  const redirectUrl = (() => {
    const normalized = normalizeRedirectPath(searchParams.get("redirect"));
    if (!normalized || normalized === "/" || normalized === ROUTES.LOGIN)
      return null;
    return normalized;
  })();
  const [rememberUser, setRememberUser] = useLocalStorage("remember", false);
  const [username, setUsername] = useLocalStorage("username", "");
  const { user } = useAuth();
  const {
    mutate: login,
    isError,
    error,
    isSuccess,
    data: loginData,
  } = useLogin({
    redirect: redirectUrl,
    // An invite page accepts its invitation once a session exists, so after a
    // second factor the code screen must bring the user back here.
    mfaResume: token ? `${pathname}${search}` : null,
  });
  const [isHelpModalOpen, setIsHelpModalOpen] = useState(false);

  const {
    register,
    getValues,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginPayload>({
    mode: "onChange",
    defaultValues: {
      username: email || "",
    },
  });
  const { data: configurations } = useConfigurations();
  // Defaulted rather than read through `configurations?.`: that guards a missing object, not a
  // missing field, and a body without the field crashed this page.
  const enabledAuthSystems = configurations?.enabledAuthSystems ?? [];

  // Extract query params for error messages
  const errorMsg = searchParams.get("error");

  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    // Only a real session counts: an mfaRequired answer has no session yet,
    // and a caller acting on it (an invite acceptance) would be refused.
    if (isSuccess && loginData && !loginData.mfaRequired && onSuccess) {
      onSuccess();
    }
  }, [isSuccess, loginData, onSuccess]);

  // If you're already logged in, redirect to target or "/activity"
  if (user?.session && pathname === ROUTES.LOGIN) {
    const blockAccess = shouldBlockAccess(user);
    if (blockAccess) {
      return <Navigate to={ROUTES.SELF_SIGNUP} replace />;
    }
    const destination = redirectUrl || ROUTES.ACTIVITY;
    if (isBackendRoute(destination)) {
      return <BackendRedirect to={destination} />;
    }
    return <Navigate to={destination} replace />;
  }

  const handleLogin = (form: LoginPayload): void => {
    delete form.remember; // We don't need to send remember for payload
    const checked = getValues("remember");
    // If "Remember Me" checked, save last username entered before login
    checked && setUsername(form.username);
    if (token) {
      login({ ...form, token });
    } else {
      login(form);
    }
  };

  // Save username and last checked status of "Remember Me" into localStorage
  const handleRememberUser = (e): void => {
    const checked = e.target.checked;
    setRememberUser(checked);
    setUsername(checked ? getValues("username") : "");
  };

  return (
    <>
      <div className="dotted flex min-h-full flex-1 flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <Link to="/" target="blank">
            <img
              className="mx-auto h-10 w-auto duration-200 ease-in-out hover:rotate-180"
              src={Logo}
              alt="Foundry Connect logo"
            />
          </Link>
          <h2 className="via-h2 text-center text-2xl font-bold leading-9 tracking-tight">
            Sign in to your account
          </h2>
          {selfSignIn && organizationName && (
            <div className="flex justify-center items-center mt-8 gap-2 text-2xl font-semibold">
              <BuildingOfficeIcon className="w-7 h-7" />
              <h2>{organizationName}</h2>
            </div>
          )}
        </div>
        <div className="mt-10 sm:mx-auto sm:w-full sm:max-w-[480px]">
          <div className="bg-white px-6 py-12 shadow sm:rounded-lg sm:px-12">
            {errorMsg && (
              <MessageBanner
                type={BannerType.Error}
                title="Login Failed"
                messages={errorMsg}
              />
            )}
            {/* Render Login Form if 'ldap' or 'local' is enabled */}
            {(enabledAuthSystems.includes(AuthTypes.LDAP) ||
              enabledAuthSystems.includes(AuthTypes.Local)) && (
              <form
                className="space-y-6"
                onSubmit={e => {
                  e.preventDefault();
                  void handleSubmit(handleLogin)(e);
                }}>
                <div>
                  <label className="block text-sm font-medium leading-6 text-gray-900">
                    Email
                  </label>
                  <div className="mt-2">
                    <input
                      {...register("username", loginOptions.username)}
                      disabled={selfSignIn}
                      defaultValue={username}
                      autoComplete="on"
                      className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                    />
                    {errors.username && (
                      <span className="text-xs text-rose-500">
                        {errors.username.message}
                      </span>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium leading-6 text-gray-900">
                    Password
                  </label>
                  <div className="mt-2 relative">
                    <input
                      id="login-password"
                      {...register("password", loginOptions.password)}
                      type={showPassword ? "text" : "password"}
                      autoComplete="current-password"
                      className="block w-full rounded-md border-0 py-1.5 pr-10 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                    />

                    <button
                      type="button"
                      aria-label={
                        showPassword ? "Hide password" : "Show password"
                      }
                      aria-controls="login-password"
                      aria-pressed={showPassword}
                      onClick={() => setShowPassword(prev => !prev)}
                      className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 hover:text-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-600 rounded">
                      {showPassword ? (
                        <EyeSlashIcon className="h-5 w-5" />
                      ) : (
                        <EyeIcon className="h-5 w-5" />
                      )}
                    </button>

                    {errors.password && (
                      <span className="text-xs text-rose-500">
                        {errors.password.message}
                      </span>
                    )}
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <input
                      {...register("remember")}
                      type="checkbox"
                      defaultChecked={rememberUser}
                      onChange={e => {
                        handleRememberUser(e);
                      }}
                      className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                    />
                    <label className="ml-3 block text-sm leading-6 text-gray-900">
                      Remember me
                    </label>
                  </div>
                  {/* Render "Forgot Password" link if enabledAuthSystems has 'local' and ENABLE_EMAIL_NOTIFICATIONS is true */}
                  {enabledAuthSystems.includes(AuthTypes.Local) &&
                    configurations?.ENABLE_EMAIL_NOTIFICATIONS && (
                      <div className="text-sm leading-6">
                        <Link
                          to="/reset-password"
                          className="font-semibold text-indigo-600 hover:text-indigo-500">
                          Forgot password?
                        </Link>
                      </div>
                    )}
                </div>
                {isError && (
                  <span className="text-xs font-semibold text-rose-500">
                    {error?.response?.data.message || "An Error Occurred"}
                  </span>
                )}
                <button
                  type="submit"
                  className="flex w-full justify-center rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">
                  Sign in
                </button>

                {configurations?.ENABLE_WALKINS_WELCOME && (
                  <>
                    <div className="relative my-6">
                      <div className="absolute inset-0 flex items-center">
                        <div className="w-full border-t border-gray-300" />
                      </div>
                      <div className="relative flex justify-center text-xs uppercase">
                        <span className="bg-white px-2 text-gray-500">or</span>
                      </div>
                    </div>

                    <Link
                      to={ROUTES.SELF_SIGNUP}
                      className="flex w-full justify-center rounded-md bg-white px-3 py-1.5 text-sm font-semibold leading-6 text-indigo-600 shadow-sm ring-1 ring-inset ring-indigo-600 hover:bg-indigo-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">
                      Create an account
                    </Link>
                  </>
                )}
              </form>
            )}
            {/* Render SSO Buttons */}
            {!selfSignIn && (
              <div className="my-6 flex flex-row justify-between space-x-2">
                {enabledAuthSystems.includes(AuthTypes.Google) && (
                  <a
                    href={`${Config.ROUTER_BASENAME}${AUTH_LINKS.GOOGLE_SSO}`}
                    onClick={() => {
                      if (redirectUrl) setPostLoginRedirect(redirectUrl);
                    }}
                    className="sso-button justify-center">
                    <img alt="Google Logo" className="h-6" src={GoogleLogo} />
                    <span className="color-[#DB4437] text-xs font-semibold leading-6">
                      Google
                    </span>
                  </a>
                )}
                {enabledAuthSystems.includes(AuthTypes.Github) && (
                  <a
                    href={`${Config.ROUTER_BASENAME}${AUTH_LINKS.GITHUB_SSO}`}
                    onClick={() => {
                      if (redirectUrl) setPostLoginRedirect(redirectUrl);
                    }}
                    className="sso-button justify-center">
                    <img alt="Github Logo" className="h-6" src={GithubLogo} />
                    <span className="text-xs font-semibold leading-6">
                      GitHub
                    </span>
                  </a>
                )}
                {enabledAuthSystems.includes(AuthTypes.SAML) && (
                  <a
                    href={`${Config.ROUTER_BASENAME}${AUTH_LINKS.SAML_SSO}`}
                    onClick={() => {
                      if (redirectUrl) setPostLoginRedirect(redirectUrl);
                    }}
                    className="sso-button justify-center">
                    <img alt="Saml Logo" className="h-6" src={SamlLogo} />
                    <span className="text-xs font-semibold leading-6">SSO</span>
                  </a>
                )}
              </div>
            )}
            {configurations?.HELP_TEXT && (
              <div className="flex items-center justify-center">
                <div className="text-sm leading-6">
                  <button
                    type="button"
                    onClick={() => setIsHelpModalOpen(true)}
                    className="font-semibold text-indigo-600 hover:text-indigo-500">
                    Need Help?
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      {isHelpModalOpen && (
        <Modal
          open={isHelpModalOpen}
          setOpen={setIsHelpModalOpen}
          title="Need Help"
          width={700}
          showButtons={false}>
          <div className="text-sm text-gray-500">
            <p
              dangerouslySetInnerHTML={createMarkup(
                configurations?.HELP_TEXT ||
                  '<p className="text-sm text-gray-500">For assistance, please contact your administrator.</p>'
              )}
            />
          </div>
        </Modal>
      )}
    </>
  );
}
